import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ServiceForm = ({ service, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: 'Conseil',
    icon: 'Briefcase',
    image: '',
    imageAlt: '',
    description: '',
    fullDescription: '',
    features: [],
    deliveryTime: '',
    startingPrice: '',
    basePrice: 0,
    industries: [],
    businessSizes: [],
    stats: '',
    color: 'var(--color-primary)'
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    if (service) {
      setFormData({
        title: service?.title || '',
        category: service?.category || 'Conseil',
        icon: service?.icon || 'Briefcase',
        image: service?.image || '',
        imageAlt: service?.imageAlt || '',
        description: service?.description || '',
        fullDescription: service?.fullDescription || '',
        features: service?.features || [],
        deliveryTime: service?.deliveryTime || '',
        startingPrice: service?.startingPrice || '',
        basePrice: service?.basePrice || 0,
        industries: service?.industries || [],
        businessSizes: service?.businessSizes || [],
        stats: service?.stats || '',
        color: service?.color || 'var(--color-primary)'
      });
    }
  }, [service]);

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSubmit(formData);
  };

  const addFeature = () => {
    if (featureInput?.trim()) {
      setFormData(prev => ({ ...prev, features: [...prev?.features, featureInput?.trim()] }));
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prev => ({ ...prev, features: prev?.features?.filter((_, i) => i !== index) }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-premium border border-border max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between">
          <h2 className="text-xl md:text-2xl font-bold text-foreground">
            {service ? 'Modifier le Service' : 'Ajouter un Service'}
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <Icon name="X" className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4 md:space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Titre"
              value={formData?.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e?.target?.value }))}
              required
            />
            <Select
              label="Catégorie"
              value={formData?.category}
              onChange={(e) => setFormData(prev => ({ ...prev, category: e?.target?.value }))}
              options={[
                { value: 'Conseil', label: 'Conseil' },
                { value: 'Juridique', label: 'Juridique' },
                { value: 'Immobilier', label: 'Immobilier' },
                { value: 'Technologie', label: 'Technologie' },
                { value: 'Événements', label: 'Événements' },
                { value: 'Fournitures', label: 'Fournitures' },
                { value: 'Transport', label: 'Transport' },
                { value: 'Formation', label: 'Formation' }
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Icône (nom Lucide)"
              value={formData?.icon}
              onChange={(e) => setFormData(prev => ({ ...prev, icon: e?.target?.value }))}
              placeholder="Briefcase"
            />
            <Input
              label="Couleur"
              type="text"
              value={formData?.color}
              onChange={(e) => setFormData(prev => ({ ...prev, color: e?.target?.value }))}
              placeholder="var(--color-primary)"
            />
          </div>

          <Input
            label="URL de l'image"
            value={formData?.image}
            onChange={(e) => setFormData(prev => ({ ...prev, image: e?.target?.value }))}
            placeholder="https://..."
          />

          <Input
            label="Texte alternatif de l'image"
            value={formData?.imageAlt}
            onChange={(e) => setFormData(prev => ({ ...prev, imageAlt: e?.target?.value }))}
            placeholder="Description de l'image"
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description courte</label>
            <textarea
              value={formData?.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e?.target?.value }))}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              rows="2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description complète</label>
            <textarea
              value={formData?.fullDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, fullDescription: e?.target?.value }))}
              className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              rows="4"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Caractéristiques</label>
            <div className="flex gap-2 mb-2">
              <Input
                value={featureInput}
                onChange={(e) => setFeatureInput(e?.target?.value)}
                placeholder="Ajouter une caractéristique"
                onKeyPress={(e) => e?.key === 'Enter' && (e?.preventDefault(), addFeature())}
              />
              <Button type="button" onClick={addFeature} iconName="Plus" />
            </div>
            <div className="space-y-2">
              {formData?.features?.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 bg-muted p-2 rounded-lg">
                  <span className="flex-1 text-foreground">{feature}</span>
                  <button type="button" onClick={() => removeFeature(index)} className="text-red-500 hover:text-red-600">
                    <Icon name="X" className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Délai de livraison"
              value={formData?.deliveryTime}
              onChange={(e) => setFormData(prev => ({ ...prev, deliveryTime: e?.target?.value }))}
              placeholder="2-3 semaines"
            />
            <Input
              label="Prix de départ"
              value={formData?.startingPrice}
              onChange={(e) => setFormData(prev => ({ ...prev, startingPrice: e?.target?.value }))}
              placeholder="À partir de 500 000 CDF"
            />
          </div>

          <Input
            label="Prix de base (numérique)"
            type="number"
            value={formData?.basePrice}
            onChange={(e) => setFormData(prev => ({ ...prev, basePrice: parseFloat(e?.target?.value) || 0 }))}
          />

          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">
              Annuler
            </Button>
            <Button type="submit" iconName="Save" iconPosition="left" className="w-full sm:w-auto">
              {service ? 'Modifier' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceForm;